import $ from 'jquery';
import noUiSlider from 'nouislider';
import wNumb from 'wnumb';

declare global {
  interface Window {
    prestashop: any;
  }
}

interface SliderSpecifications {
  currencySymbol: string;
  positivePattern: string;
}

class RangeSlider {
  private $slider: JQuery;
  private min: number;
  private max: number;
  private $parentContainer: JQuery;
  private $inputs: JQuery[];
  private sliderType: 'price' | 'weight';
  private sign: string;
  private positivePattern: string;
  private values: number[];
  private signPosition: 'prefix' | 'suffix';
  private format: any;
  private sliderHandler: any;

  constructor(element: HTMLElement) {
    this.$slider = $(element);

    this.setConfig();
    this.setFormat();
    this.initFilersSlider();
    this.setEvents();
  }

  private getSliderType(): void {
    this.sliderType = this.$slider.data('slider-specifications') ? 'price' : 'weight';
  }

  private setConfig(): void {
    this.min = this.$slider.data('slider-min');
    this.max = this.$slider.data('slider-max');
    this.$parentContainer = this.$slider.closest('.js-input-range-slider-container');
    this.$inputs = [
      this.$parentContainer.find('[data-action="range-from"]'),
      this.$parentContainer.find('[data-action="range-to"]')
    ];

    this.getSliderType();

    if (this.sliderType === 'price') {
      const specifications: SliderSpecifications = this.$slider.data('slider-specifications');
      this.sign = specifications.currencySymbol;
      this.positivePattern = specifications.positivePattern;
      this.values = this.$slider.data('slider-values');
      this.signPosition = this.positivePattern.indexOf('¤') === 0 ? 'prefix' : 'suffix';
    } else if (this.sliderType === 'weight') {
      const unit = this.$slider.data('slider-unit');
      this.sign = unit;
      this.values = this.$slider.data('slider-values');
      this.signPosition = 'suffix';
    }

    // Parse values if they're a JSON string
    if (typeof this.values === 'string') {
      try {
        this.values = JSON.parse(this.values);
      } catch (e) {
        console.warn('Failed to parse slider values:', this.values);
        this.values = [this.min, this.max];
      }
    }

    if (!Array.isArray(this.values)) {
      this.values = [this.min, this.max];
    }

    // Ensure values are within range and properly set
    if (this.values[0] < this.min) this.values[0] = this.min;
    if (this.values[1] > this.max) this.values[1] = this.max;
    if (this.values[0] >= this.values[1]) {
      this.values = [this.min, this.max];
    }

    console.log('Slider config:', {
      min: this.min,
      max: this.max,
      values: this.values,
      sign: this.sign,
      signPosition: this.signPosition
    });
  }

  private setFormat(): void {
    this.format = wNumb({
      mark: ',',
      thousand: ' ',
      decimals: 0,
      [this.signPosition]: this.signPosition === 'prefix' ? this.sign : ` ${this.sign}`,
    });
  }

  private initFilersSlider(): void {
    console.log('Creating noUiSlider with config:', {
      start: this.values,
      connect: [false, true, false],
      range: {
        min: this.min,
        max: this.max,
      },
      format: this.format,
    });

    try {
      this.sliderHandler = noUiSlider.create(this.$slider.get(0), {
        start: this.values,
        connect: [false, true, false],
        range: {
          min: this.min,
          max: this.max,
        },
        format: this.format,
      });
      console.log('NoUiSlider created successfully');
    } catch (error) {
      console.error('Failed to create noUiSlider:', error);
    }
  }

  private setInputValues(values: any[], formatValue = false): void {
    this.$inputs.forEach((input, i) => {
      const val = formatValue ? this.format.from(values[i]) : values[i];
      // always keep plain numeric in inputs
      $(input).val(typeof val === 'number' ? val : this.format.from(val));
    });
  }

  private setEvents(): void {
    this.sliderHandler.off('set', RangeSlider.handlerSliderSet);
    this.sliderHandler.on('set', RangeSlider.handlerSliderSet);
    this.sliderHandler.off('update', this.handlerSliderUpdate);
    this.sliderHandler.on('update', this.handlerSliderUpdate);

    this.$inputs.forEach(($input) => {
      $input.off('focus', this.handleInputFocus);
      $input.on('focus', this.handleInputFocus);
      $input.off('blur', this.handleInputBlur);
      $input.on('blur', this.handleInputBlur);
      $input.on('keyup', this.handleInputKeyup);
    });
  }

  private static getInputAction($input: JQuery): string {
    return $input.data('action');
  }

  private getInputPositionInValue($input: JQuery): number {
    const actionPosition: Record<string, number> = {
      'range-from': 0,
      'range-to': 1,
    };

    return actionPosition[RangeSlider.getInputAction($input)];
  }

  private handleInputFocus = ({target}: any): void => {
    const $input = $(target);
    $input.val(this.format.from($input.val()));
  };

  private handleInputBlur = ({target}: any): void => {
    const $input = $(target);
    const value = $input.val();
    const position = this.getInputPositionInValue($input);
    const oldValues = this.values;
    const newValues = [...oldValues];
    newValues[position] = value as number;

    if (value !== oldValues[position]) {
      this.sliderHandler.set(newValues);
    } else {
      const num = parseFloat($input.val() as string);
      $input.val(Number.isFinite(num) ? num : oldValues[position]);
    }
  };

  private handleInputKeyup = ({target, keyCode}: any): void => {
    if (keyCode !== 13) {
      return;
    }
    const $input = $(target);
    const value = $input.val();
    const position = this.getInputPositionInValue($input);
    const oldValues = this.values;
    const newValues = [...oldValues];
    newValues[position] = value as number;

    if (value !== oldValues[position]) {
      this.sliderHandler.set(newValues);
    } else {
      const num = parseFloat($input.val() as string);
      $input.val(Number.isFinite(num) ? num : oldValues[position]);
    }
  };

  private handlerSliderUpdate = (values: any[]): void => {
    this.setInputValues(values, true);
  };

  private static handlerSliderSet(
    values: any[],
    handle: number,
    unencoded: number[],
    tap: boolean,
    positions: number[],
    noUiSliderInstance: any,
  ): void {
    const formatFunction = noUiSliderInstance.options.format;
    const [from, to] = values.map((val: any) => formatFunction.from(val));

    const $target = $(noUiSliderInstance.target);
    const $container = $target.closest('.js-input-range-slider-container');
    const $fromInput = $container.find('[data-action="range-from"]');
    const $toInput = $container.find('[data-action="range-to"]');
    
    $fromInput.val(from).trigger('change');
    $toInput.val(to).trigger('change');

    const filtersForm = document.getElementById('mp-offers-filters') as HTMLFormElement;
    if (filtersForm) {
      const filterChangeEvent = new CustomEvent('marketplaceFilterChange', {
        detail: { source: 'priceSlider' }
      });
      filtersForm.dispatchEvent(filterChangeEvent);
    }
  }
}

export default RangeSlider;
